import type { ProgramInitiation } from '../types';

/**
 * How far along is a program?
 *
 * ⚠️⚠️ **A PROGRAM'S COMPLETION IS ITS RUN'S, NOT ITS OWN.** A `Program` is a
 * *definition* — a set of modules and a schedule — and a `ProgramInitiation` is
 * one *run* of it. Nothing has ever written `Program.programStatus = 'completed'`
 * (verified across all history), so every reader that tested for it was
 * permanently false: the Status → Progress "Programs" tile read `0 done` no
 * matter how many you finished.
 *
 * ⚠️⚠️ **AND A FINISHED RUN IS NOT AN ACTIVE ONE.** `programStore.getActiveInitiation`
 * filters `status === 'active'`, which is correct for the question it asks
 * ("is one in flight?") and wrong for this one. Four callers used it to read
 * *completion*, so the moment a program's last day was processed its run
 * disappeared from the lookup and the program read as **0%** — a quest goal
 * froze short of 100 and never rolled up into Vision, and an ability requirement
 * regressed from complete to nothing. Finishing something must never erase it.
 *
 * This module is the single answer, derived from the runs. Deriving rather than
 * storing a `progress` field on `Program` is deliberate: a stored copy is a
 * second source of truth, and it drifts the first time a run is edited, deleted
 * or synced from another device.
 */

/** The only three fields a standing is built from. Structural, so tests pass literals. */
export type ProgramRun = Pick<ProgramInitiation, 'programId' | 'status' | 'completionPercentage'>;

export interface ProgramStanding {
  /** 0–100 — the BEST run's reading. */
  progress: number;
  /** Has any run of this program been carried to completion? */
  completed: boolean;
  /** Is a run in flight right now? (paused does NOT count.) */
  running: boolean;
}

const clampPct = (n: number | undefined): number =>
  Math.min(100, Math.max(0, Math.round(n || 0)));

/**
 * A program's standing, or `null` when it has **never been started**.
 *
 * ⚠️ `null` is not 0. A program you wrote and have not begun is not a program
 * you failed — the same rule as "a day that asked nothing scores `null`, never
 * 0", and the same contract `deriveLinkedGoalState` already uses for a link it
 * cannot measure ("leave the manual toggle alone").
 *
 * ⭐⭐ **THE BEST RUN WINS, so the reading is MONOTONIC.** Completing a program
 * and then starting it again must not drop it from 100% back to day three's 10%.
 * This is the shipped rule "a completed quest counts as 100, it does not leave
 * the set" one rung further down: a domain's number must not fall the moment its
 * best work finishes.
 *
 * ⚠️ `partially_completed` (a run that reached its last day under 75%) is a
 * FINISHED run that fell short. It keeps its honest percentage and is *not*
 * `completed` — it neither claims success nor resets to zero.
 */
export function programStanding(
  programId: string,
  runs: ProgramRun[] | undefined | null,
): ProgramStanding | null {
  const mine = (runs || []).filter(r => r?.programId === programId);
  if (mine.length === 0) return null;

  let progress = 0;
  let completed = false;
  let running = false;

  for (const run of mine) {
    // ⚠️ A completed run is pinned to 100 rather than trusting its stored
    // percentage: `calculateCompletionStatus` calls anything at or above 75%
    // complete, and a run the user carried to the end should not read as 78%.
    const isComplete = run.status === 'completed' || clampPct(run.completionPercentage) >= 100;
    const reading = isComplete ? 100 : clampPct(run.completionPercentage);

    if (reading > progress) progress = reading;
    if (isComplete) completed = true;
    if (run.status === 'active') running = true;
  }

  return { progress, completed, running };
}
